import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Camera as CameraIcon, ArrowClockwise, CheckCircle, X, ArrowsCounterClockwise } from "@phosphor-icons/react";

export default function MobilePhoto() {
  const { token } = useParams();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | streaming | captured | uploading | sent | error
  const [sessionOk, setSessionOk] = useState(null); // null | true | false
  const [error, setError] = useState("");
  const [cameras, setCameras] = useState([]);
  const [activeCamIdx, setActiveCamIdx] = useState(0);

  // Validate session
  useEffect(() => {
    api.get(`/photo-sync/${token}/status`)
      .then((r) => {
        const expires = new Date(r.data.expires_at);
        if (expires < new Date()) {
          setSessionOk(false);
          setError("This session has expired. Generate a new QR on the desktop.");
        } else {
          setSessionOk(true);
        }
      })
      .catch(() => {
        setSessionOk(false);
        setError("Invalid or expired session.");
      });
  }, [token]);

  const startCamera = async (deviceId = null) => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("This browser doesn't support camera access. Try Chrome or Safari.");
      setStatus("error");
      return;
    }
    // Stop any existing stream first so we can switch cameras cleanly.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    // Try the preferred constraint first, then progressively relax. Some
    // mobile browsers reject `facingMode` or the resolution hints with
    // NotFoundError/OverconstrainedError — `{ video: true }` is the safety net.
    const attempts = [
      deviceId
        ? { video: { deviceId: { exact: deviceId } }, audio: false }
        : { video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } }, audio: false },
      deviceId
        ? { video: { deviceId: { exact: deviceId } }, audio: false }
        : { video: { facingMode: "user" }, audio: false },
      { video: true, audio: false },
    ];
    let stream = null;
    let lastErr = null;
    for (const c of attempts) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(c);
        break;
      } catch (err) {
        lastErr = err;
        // Permission denial is final — no point retrying.
        if (err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError") break;
      }
    }
    if (!stream) {
      const name = lastErr?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setError("Camera access denied. Please allow camera permissions and try again.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setError("No camera found on this device.");
      } else if (name === "NotReadableError") {
        setError("Camera is in use by another app. Close it and try again.");
      } else if (name === "SecurityError") {
        setError("Camera blocked — open this page in your browser (not an in-app webview).");
      } else {
        setError(`Camera error: ${name || lastErr?.message || "unknown"}`);
      }
      setStatus("error");
      return;
    }
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      try {
        await videoRef.current.play();
      } catch {}
    }
    // Enumerate devices once permission is granted (labels are only available then).
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter((d) => d.kind === "videoinput");
      setCameras(cams);
      if (deviceId) {
        const idx = cams.findIndex((c) => c.deviceId === deviceId);
        if (idx >= 0) setActiveCamIdx(idx);
      }
    } catch {}
    setStatus("streaming");
  };

  const switchCamera = async () => {
    if (cameras.length < 2) return;
    const next = (activeCamIdx + 1) % cameras.length;
    setActiveCamIdx(next);
    await startCamera(cameras[next].deviceId);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => stopCamera(), []);

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const size = Math.min(v.videoWidth, v.videoHeight);
    c.width = 640;
    c.height = 640;
    const sx = (v.videoWidth - size) / 2;
    const sy = (v.videoHeight - size) / 2;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, sx, sy, size, size, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", 0.85);
    setPhoto(dataUrl);
    setStatus("captured");
    stopCamera();
  };

  const retake = async () => {
    setPhoto(null);
    await startCamera();
  };

  const send = async () => {
    if (!photo) return;
    setStatus("uploading");
    try {
      await api.post(`/photo-sync/${token}/upload`, { photo_base64: photo });
      setStatus("sent");
      toast.success("Photo sent to desktop");
    } catch (e) {
      setStatus("captured");
      toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed");
    }
  };

  if (sessionOk === false) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <X size={36} weight="duotone" className="text-[var(--sms-danger)] mx-auto mb-4" />
          <div className="font-display text-2xl font-black tracking-tighter mb-2">Session expired</div>
          <div className="text-sm text-slate-500">{error}</div>
        </div>
      </div>
    );
  }

  if (sessionOk === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-slate-400">Loading session…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
      <header className="border-b border-slate-200 bg-white px-5 py-4">
        <div className="font-display text-lg font-black tracking-tighter">SMS<span className="text-[var(--sms-primary)]">.</span></div>
        <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Mobile photo capture</div>
      </header>

      <main className="flex-1 px-5 py-6 max-w-md mx-auto w-full">
        <h1 className="font-display text-3xl font-black tracking-tighter mb-2">
          {status === "sent" ? "Sent." : "Capture photo."}
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          {status === "sent"
            ? "Your photo has been delivered to the desktop. You can close this tab."
            : "Frame your face within the square. Tap capture, then send."}
        </p>

        <div className="aspect-square w-full bg-slate-900 border border-slate-900 relative overflow-hidden mb-5">
          {!photo && (
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="mobile-video" />
          )}
          {photo && (
            <img src={photo} alt="captured" className="w-full h-full object-cover" data-testid="mobile-photo-preview" />
          )}
          {!photo && status !== "streaming" && (
            <div className="absolute inset-0 flex items-center justify-center text-white/70 font-mono-sms text-[10px] uppercase tracking-[0.3em]">
              Camera idle
            </div>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {error && (
          <div className="text-xs font-mono-sms text-[var(--sms-danger)] uppercase tracking-[0.15em] mb-4">{error}</div>
        )}

        <div className="space-y-3">
          {status === "idle" || status === "error" ? (
            <button onClick={startCamera} data-testid="mobile-start-camera" className="sms-btn-primary w-full inline-flex items-center justify-center gap-2">
              <CameraIcon size={16} weight="bold" /> Open camera
            </button>
          ) : null}
          {status === "streaming" && (
            <>
              <button onClick={capture} data-testid="mobile-capture" className="sms-btn-primary w-full inline-flex items-center justify-center gap-2">
                <CameraIcon size={16} weight="bold" /> Capture
              </button>
              {cameras.length > 1 && (
                <button onClick={switchCamera} data-testid="mobile-switch-camera" className="sms-btn-secondary w-full inline-flex items-center justify-center gap-2">
                  <ArrowsCounterClockwise size={16} weight="bold" />
                  Switch camera ({activeCamIdx + 1}/{cameras.length})
                </button>
              )}
            </>
          )}
          {status === "captured" && (
            <>
              <button onClick={send} data-testid="mobile-send" className="sms-btn-primary w-full inline-flex items-center justify-center gap-2">
                <CheckCircle size={16} weight="bold" /> Send to desktop
              </button>
              <button onClick={retake} data-testid="mobile-retake" className="sms-btn-secondary w-full inline-flex items-center justify-center gap-2">
                <ArrowClockwise size={16} weight="bold" /> Retake
              </button>
            </>
          )}
          {status === "uploading" && (
            <button disabled className="sms-btn-primary w-full">Sending…</button>
          )}
          {status === "sent" && (
            <div className="text-center pt-4">
              <CheckCircle size={48} weight="duotone" className="text-[var(--sms-success)] mx-auto mb-2" />
              <div className="font-mono-sms text-xs uppercase tracking-[0.2em] text-[var(--sms-success)]">Delivered</div>
            </div>
          )}
        </div>
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
